import { TabPanel, TabView, TabViewTabChangeEvent } from 'primereact/tabview';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { useListPermissions } from '../../../../hooks/api/permissions';
import { useGetProductById } from '../../../../hooks/api/products';
import { useAuth } from '../../../../hooks/auth/useAuth';
import styles from './ProductTabView.module.css';
import PcfTab from './tabs/pcf-tab/PcfTab';
import PermissionsTab from './tabs/permissions-tab/PermissionsTab';
import ProductDetailsTab from './tabs/product-details-tab/ProductDetailsTab';

const TABS = ['product-details', 'pcf', 'permissions'];

type ProductTabViewProps = {
  isEditingDisabled?: boolean;
};

const ProductTabView = ({ isEditingDisabled = false }: ProductTabViewProps) => {
  // Read params from URL
  const { productId, footprintId } = useParams() as { productId: string; footprintId: string };

  const { user } = useAuth();

  // Fetch product details based on the productId
  const { data: product } = useGetProductById(productId, {
    enabled: !!productId,
    retry: 0,
  });

  // Fetch permissions based on the productId and user's license
  const { data: permissions = [], isLoading: isPermissionsLoading = false } = useListPermissions(
    productId,
    { enabled: user?.company?.license !== 'basic' },
  );

  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  const isPermissionTabVisible = user?.company?.license !== 'basic';

  // Based on the URL parameters, the respective tab should be selected
  // Derive the active tab index from the URL
  const activeTabIndex = useMemo(() => {
    const pathSegments = location.pathname.split('/');
    const activeTab = pathSegments[pathSegments.length - 1];

    // If footprintId is present, we should always show the 'pcf' tab
    if (footprintId) {
      return TABS.indexOf('pcf');
    }

    const index = TABS.indexOf(activeTab);

    if (index === 2 && !isPermissionTabVisible) {
      // If the permissions tab is not visible, navigate to the product details tab by default
      // This is to handle cases where the user has a basic license and tries to access the permissions tab via URL
      const href = `/products/${product?.id}/product-details`;
      navigate(href);
    }

    return Math.max(index, 0);
  }, [location.pathname, footprintId, isPermissionTabVisible]);

  const handleTabChange = (event: TabViewTabChangeEvent) => {
    const href = `/products/${product?.id}/${TABS[event.index]}`;

    navigate(href);
  };

  return (
    <TabView
      className={styles['product-tab-view']}
      pt={{
        nav: { className: 'flex flex-row gap-6' },
      }}
      activeIndex={activeTabIndex}
      onTabChange={handleTabChange}
    >
      {/* Product Details Tab */}
      <TabPanel header={t('productDetailsPage.tabs.productDetailsTab.tabHeader')}>
        <ProductDetailsTab isEditingDisabled={isEditingDisabled} />
      </TabPanel>

      {/* PCF Tab */}
      <TabPanel header={t('productDetailsPage.tabs.pcfTab.tabHeader')}>
        <PcfTab isReadOnly={isEditingDisabled} />
      </TabPanel>

      {/* Permisions */}
      <TabPanel
        header={t('productDetailsPage.tabs.permissionsTab.tabHeader')}
        visible={isPermissionTabVisible}
      >
        <PermissionsTab
          productId={productId}
          isPermissionsLoading={isPermissionsLoading}
          permissions={permissions}
        />
      </TabPanel>
    </TabView>
  );
};

export default ProductTabView;
